// "Survey Question Charts" section — 4 ChartTablePairs (engagement, contributors, responses, unique responses).
// Conditionally renders chart/button/table blocks when questionEngagementData has entries.

import React from "react";
import { Typography } from "@material-tailwind/react";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";
import {
    calculateLeftMargin,
    truncateLabel,
} from "../data-transforms";
import {
    ChartTablePair,
    COMMON_CHART_PROPS,
    buildEngagementLegend,
    makeChartTooltip,
    ResponsiveBar,
    CHART_BASE_MARGIN,
} from "./chart-table-pair";

export function QuestionChartsSection({
    questionEngagementData,
    questionByTotalEngagement,
    questionByTotalEngagementForChart,
    questionByContributors,
    questionByContributorsForChart,
    questionByResponses,
    questionByResponsesForChart,
    questionByUnique,
    questionByUniqueForChart,
    openChartTables,
    toggleChartTable,
    legendDirection,
    getLegendPosition,
    getBottomMargin,
    getBottomMarginNoLegend,
    getLegendItemWidth,
}) {
    const visible = questionEngagementData?.length > 0;
    const legendHelpers = { legendDirection, getLegendPosition, getLegendItemWidth };

    // Pre-compute label arrays used by calculateLeftMargin. Each chart
    // truncates the question text to 30 chars for the label, then the
    // left-margin calc truncates again to 10 chars for tick format.
    const buildLabels = (data) =>
        data
            .slice(0, 10)
            .map((q) => `Q${q.number}: ${truncateLabel(q.question, 30)}`)
            .map((l) => truncateLabel(l, 10));
    const totalEngagementLabels = buildLabels(questionByTotalEngagementForChart);
    const contributorsLabels = buildLabels(questionByContributorsForChart);
    const responsesLabels = buildLabels(questionByResponsesForChart);
    const uniqueLabels = buildLabels(questionByUniqueForChart);

    return (
        <div className="mb-6">
            <Typography variant="h5" className="mb-1">
                Survey Question Charts
            </Typography>

            {/* 1. Survey Engagement by Question — full-width, 3-key grouped */}
            <ChartTablePair
                title="Survey Engagement by Question"
                visible={visible}
                yAxisLabel="Questions"
                chartHeight="600px"
                toggleKey="surveyQuestionEngagement"
                openChartTables={openChartTables}
                toggleChartTable={toggleChartTable}
                className="mb-6"
                chart={
                    <ResponsiveBar
                        {...COMMON_CHART_PROPS}
                        data={questionByTotalEngagementForChart.slice(0, 10).map((q) => ({
                            label: `Q${q.number}: ${truncateLabel(q.question, 30)}`,
                            number: q.number,
                            question: q.question,
                            contributors: q.contributors,
                            responses: q.responses,
                            uniqueResponses: q.uniqueResponses,
                        }))}
                        groupMode="grouped"
                        keys={["contributors", "responses", "uniqueResponses"]}
                        margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMargin(3), left: calculateLeftMargin(totalEngagementLabels, { charWidth: 5, padding: 15 }) }}
                        colors={["#fdc403", "#aaa9a9", "#c5c5c5"]}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: "Count",
                            legendPosition: "middle",
                            legendOffset: 40,
                        }}
                        labelPosition="end"
                        labelOffset={4}
                        legends={buildEngagementLegend(
                            ["contributors", "responses", "uniqueResponses"],
                            ["#fdc403", "#aaa9a9", "#c5c5c5"],
                            legendHelpers,
                        )}
                        tooltip={makeChartTooltip(20)}
                    />
                }
                table={
                    <GobbyTable>
                        <GobbyTableHead>
                            <GobbyTableRow>
                                <GobbyTableHeader>Question</GobbyTableHeader>
                                <GobbyTableHeader>Contributors</GobbyTableHeader>
                                <GobbyTableHeader>Responses</GobbyTableHeader>
                                <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                            </GobbyTableRow>
                        </GobbyTableHead>
                        <GobbyTableBody>
                            {questionByTotalEngagement.map((q, index) => (
                                <GobbyTableRow key={index}>
                                    <GobbyTableCell>Q{q.number}: {truncateLabel(q.question, 40)}</GobbyTableCell>
                                    <GobbyTableCell>{q.contributors}</GobbyTableCell>
                                    <GobbyTableCell>{q.responses}</GobbyTableCell>
                                    <GobbyTableCell>{q.uniqueResponses}</GobbyTableCell>
                                </GobbyTableRow>
                            ))}
                        </GobbyTableBody>
                    </GobbyTable>
                }
            />

            {/* 2-4. Three single-metric charts in a 2-col grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">

                {/* 2. Survey Contributors by Question */}
                <ChartTablePair
                    title="Survey Contributors by Question"
                    visible={visible}
                    yAxisLabel="Questions"
                    chartHeight="400px"
                    toggleKey="surveyQuestionContributors"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={questionByContributorsForChart.slice(0, 10).map((q) => ({
                                label: `Q${q.number}: ${truncateLabel(q.question, 30)}`,
                                contributors: q.contributors,
                            }))}
                            keys={["contributors"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(contributorsLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#fdc403"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Contributors",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                            tooltip={makeChartTooltip(20)}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Question</GobbyTableHeader>
                                    <GobbyTableHeader>Contributors</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {questionByContributors.map((q, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>Q{q.number}: {truncateLabel(q.question, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{q.contributors}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />

                {/* 3. Survey Responses by Question */}
                <ChartTablePair
                    title="Survey Responses by Question"
                    visible={visible}
                    yAxisLabel="Questions"
                    chartHeight="400px"
                    toggleKey="surveyQuestionResponses"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={questionByResponsesForChart.slice(0, 10).map((q) => ({
                                label: `Q${q.number}: ${truncateLabel(q.question, 30)}`,
                                responses: q.responses,
                            }))}
                            keys={["responses"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(responsesLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#aaa9a9"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Responses",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                            tooltip={makeChartTooltip(20)}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Question</GobbyTableHeader>
                                    <GobbyTableHeader>Responses</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {questionByResponses.map((q, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>Q{q.number}: {truncateLabel(q.question, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{q.responses}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />

                {/* 4. Survey Unique Responses by Question */}
                <ChartTablePair
                    title="Survey Unique Responses by Question"
                    visible={visible}
                    yAxisLabel="Questions"
                    chartHeight="400px"
                    toggleKey="surveyQuestionUniqueResponses"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={questionByUniqueForChart.slice(0, 10).map((q) => ({
                                label: `Q${q.number}: ${truncateLabel(q.question, 30)}`,
                                uniqueResponses: q.uniqueResponses,
                            }))}
                            keys={["uniqueResponses"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(uniqueLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#c5c5c5"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Unique Responses",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                            tooltip={makeChartTooltip(20)}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Question</GobbyTableHeader>
                                    <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {questionByUnique.map((q, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>Q{q.number}: {truncateLabel(q.question, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{q.uniqueResponses}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />
            </div>
        </div>
    );
}
